/**
 * Centralized query key management for TanStack Query
 *
 * This file provides a single source of truth for all query keys used across the app.
 * Following the pattern: [scope, ...identifiers] to ensure proper cache invalidation.
 */

// Root key for all app queries
const all = ['loadline'] as const

// User-scoped data that depends on authentication
const user = (userId: string) => [...all, 'user', userId] as const

export const queryKeys = {
  // Root key for all app queries
  all,

  // User-scoped data that depends on authentication
  user,

  userCredentials: (userId: string) => [...user(userId), 'credentials'] as const,

  userSettings: (userId: string) => [...user(userId), 'settings'] as const,

  userCSVUpload: (userId: string) => [...user(userId), 'csv-upload'] as const,

  csvWorkoutData: (userId: string, uploadDate: string) =>
    [...user(userId), 'csv-workout-data', uploadDate] as const,

  // Workout data that depends on data source and API key
  workouts: {
    all: [...all, 'workouts'] as const,
    bySource: (dataSource: string, apiKeyHash: string, dateKey?: string) =>
      dateKey 
        ? [...all, 'workouts', dataSource, apiKeyHash, dateKey] as const
        : [...all, 'workouts', dataSource, apiKeyHash] as const,
    infinite: (dataSource: string, apiKeyHash: string, pageSize: number) =>
      [...all, 'workouts', dataSource, apiKeyHash, 'infinite', pageSize] as const
  },

  // Routines data
  routines: (apiKey: string) => [...all, 'routines', apiKey] as const,

  // Routine folders data
  routineFolders: (apiKey: string) => [...all, 'routine-folders', apiKey] as const,

  // Exercise templates data
  exerciseTemplates: (apiKeyHash: string) => [...all, 'exercise-templates', apiKeyHash] as const
} as const

// Helper functions for common invalidation patterns
export const invalidationHelpers = {
  // Invalidate all user data when user changes
  allUserData: (userId: string) => user(userId),

  // Invalidate workout data when API key or data source changes
  allWorkouts: () => queryKeys.workouts.all,

  // Invalidate everything (use sparingly)
  everything: () => all
} as const
